import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';

import { ReactComponent as HandSvg } from '@app/pages/NFTDetailView/assets/icons/gradient-hand.svg';
import BellPngMobile from '@app/pages/NFTDetailView/assets/images/bell-mobile.png';
import BellPng from '@app/pages/NFTDetailView/assets/images/bell.png';

/* Wrapper
------------------------- */

export const Wrapper = styled(motion.div)`
    position: fixed;
    bottom: 0;
    right: 0;
    z-index: 10;
`;

/* Shadow
------------------------- */

export const Shadow = styled.div`
    position: absolute;
    z-index: 0;
    bottom: 0;
    right: 0;
    width: 500px;
    height: 500px;
    pointer-events: none;

    background: radial-gradient(circle at 80% 88%, #090719 0%, rgba(0, 0, 0, 0) 44%);

    display: none;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        display: block;
    }
`;

/* Pulse
------------------------- */

export const Pulse = styled.div<{ $hasNotification: boolean }>`
    position: absolute;
    z-index: 1;

    border-radius: 100%;
    pointer-events: none;

    &::before,
    &::after {
        content: '';

        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;

        width: 100%;
        height: 100%;
        border-radius: 100%;
        background-color: #ffce22;

        transform: scale(1.2);
        opacity: 0.05;

        transform-origin: center center;
    }

    width: 46px;
    height: 46px;
    bottom: 78px;
    right: 28px;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 82px;
        height: 82px;
        bottom: 55px;
        right: 55px;
    }
`;

/* Button
------------------------- */

export const Border = styled.div<{ $hasNotification: boolean }>`
    border-radius: 100%;
    padding: 1px;
    background: linear-gradient(180deg, #5d502f 0%, #20181b 100%);
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;

    ${({ $hasNotification }) =>
        $hasNotification &&
        css`
            background: linear-gradient(180deg, #b49527 0%, #574515 100%);
        `}
`;

export const Button = styled.button<{ $hasNotification: boolean }>`
    width: 100%;
    height: 100%;
    border-radius: 100%;
    overflow: hidden;

    z-index: 2;
    position: relative;
    border: none;
    outline: none;

    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    pointer-events: all;

    background: linear-gradient(180deg, #3f3016 0%, #20181b 100%);

    &::after {
        content: '';
        position: absolute;
        z-index: 0;
        top: 0;
        right: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(180deg, #a17d0c 0%, #5d4914 100%);
        transition: opacity 300ms ease-in-out;
        opacity: 0;
    }

    ${({ $hasNotification }) =>
        $hasNotification &&
        css`
            &::after {
                opacity: 1;
            }
        `}
`;

export const Icon = styled(motion.div)`
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ringBellAnimation = keyframes`
    0% { transform: rotate(-25deg); }
    20% { transform: rotate(20deg); }
    40% { transform: rotate(-15deg); }
    60% { transform: rotate(10deg); }
    80% { transform: rotate(-5deg); }
    100% { transform: rotate(0deg); }
`;

const waveHandAnimation = keyframes`
    0% { transform: rotate(-10deg); }
    25% { transform: rotate(10deg); }
    50% { transform: rotate(-10deg); }
    75% { transform: rotate(10deg); }
    100% { transform: rotate(0deg); }
`;

export const BellIcon = styled.div`
    width: 20px;
    height: 20px;
    transform-origin: top center;
    animation: ${ringBellAnimation} 1s ease-in-out;
    background: url(${BellPngMobile}) no-repeat center center;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 39px;
        height: 39px;
        background: url(${BellPng}) no-repeat center center;
    }
`;

export const HandIcon = styled(HandSvg)`
    width: 20px;
    transform-origin: bottom center;
    animation: ${waveHandAnimation} 1s ease-in-out;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 36px;
    }
`;

/* Count
------------------------- */

export const Count = styled(motion.div)`
    position: absolute;
    z-index: 3;
    top: -7px;
    right: -7px;

    height: 22px;
    min-width: 22px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #fff;
    font-weight: 700;
    font-size: 10px;
    line-height: 100%;
    letter-spacing: -0.03em;

    border-radius: 50px;
    background: linear-gradient(103.57deg, #aa039f -53.05%, #ed014d 23.36%, #f67916 101.37%);
    border: 2px solid #121322;

    padding: 0 4px;
    pointer-events: none;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        height: 28px;
        min-width: 28px;
        border: 3px solid #121322;
        padding: 0 4px;
        font-size: 12px;
        top: -5px;
        right: -5px;
    }
`;

/* Notifications
------------------------- */

export const Notifications = styled(motion.div)`
    position: absolute;
    z-index: 1;
    bottom: 100%;
    right: -10px;

    display: flex;
    flex-direction: column-reverse;
    gap: 5px;
`;

export const Notification = styled(motion.div)`
    display: flex;
    align-items: center;
    align-self: flex-start;
    width: 100%;
    justify-content: flex-start;
    gap: 8px;

    padding: 3px;
    background: rgba(0, 0, 0, 0.6);
    box-shadow: 12.2981px 12.2981px 18.4472px rgba(0, 0, 0, 0.25);
    backdrop-filter: blur(24.5963px);
    border-radius: 6px;
    pointer-events: all;
    cursor: pointer;
    overflow: hidden;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: 6px;
        border-radius: 12px;
    }
`;

export const NotificationImage = styled.div<{ $image?: string }>`
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background-size: cover;
    background-position: center center;
    background-color: #000;
    flex-shrink: 0;

    ${({ $image }) =>
        $image &&
        css`
            background-image: url(${$image});
        `}

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 56px;
        height: 56px;
        border-radius: 12px;
    }
`;

export const NotificationText = styled(motion.div)`
    font-weight: 700;
    font-size: 11px;
    line-height: 105%;
    text-align: left;
    letter-spacing: -0.02em;
    white-space: nowrap;
    padding-right: 10px;
    transform-origin: left center;

    color: #fff;

    span {
        opacity: 0.5;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 16px;
    }
`;
